import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback((u) => {
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // An expired server session still signs out cleanly in the client — log
      // for debugging but never block the local sign-out that follows.
      console.debug("Server logout call failed; clearing client session", e);
    }
    setUser(null);
  }, []);

  const isPaid = user?.plan === "master" || user?.role === "admin";

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, isPaid }),
    [user, loading, login, logout, refresh, isPaid],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
